import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";

interface ChoiceBoardAdvancedData {
  preferredLearningStyles?: string[];
  difficultyDistribution?: string;
  selectionRequirements?: string;
  learningOutcomesFocus?: string[];
}

export const DifficultyDistributionStep: React.FC<
  StepComponentProps<ChoiceBoardAdvancedData>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomDistribution, setShowCustomDistribution] = useState(false);

  const predefinedDistributions = [
    {
      id: "balanced",
      emoji: "⚖️",
      label: t("choiceBoards.difficulty.balanced"),
      desc: t("choiceBoards.difficulty.balancedDesc"),
    },
    {
      id: "same_level",
      emoji: "📏",
      label: t("choiceBoards.difficulty.sameLevel"),
      desc: t("choiceBoards.difficulty.sameLevelDesc"),
    },
    {
      id: "progressive",
      emoji: "📈",
      label: t("choiceBoards.difficulty.progressive"),
      desc: t("choiceBoards.difficulty.progressiveDesc"),
    },
  ];

  const handleDistributionSelect = (distributionId: string) => {
    onDataChange({ difficultyDistribution: distributionId });
  };

  const handleCustomDistributionToggle = () => {
    const currentDistribution = data.difficultyDistribution;
    const hasCustomDistribution =
      currentDistribution &&
      !predefinedDistributions.some(
        (option) => option.id === currentDistribution,
      );

    if (hasCustomDistribution || showCustomDistribution) {
      onDataChange({ difficultyDistribution: "" });
      setShowCustomDistribution(false);
    } else {
      setShowCustomDistribution(true);
    }
  };

  const handleCustomDistributionChange = (value: string) => {
    onDataChange({ difficultyDistribution: value });
  };

  const isDistributionSelected = (distributionId: string) => {
    return data.difficultyDistribution === distributionId;
  };

  const isCustomDistributionSelected = () => {
    const currentDistribution = data.difficultyDistribution;
    return (
      showCustomDistribution ||
      (currentDistribution &&
        !predefinedDistributions.some(
          (option) => option.id === currentDistribution,
        ))
    );
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("choiceBoards.steps.difficultyDistribution.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("choiceBoards.steps.difficultyDistribution.description")}
        </p>
      </div>

      {/* Distribution Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("choiceBoards.form.difficultyDistribution")}
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {predefinedDistributions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isDistributionSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleDistributionSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    isDistributionSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isDistributionSelected(option.id) && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Distribution Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomDistributionSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomDistributionToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isCustomDistributionSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomDistributionSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-medium text-base">
                    {t("choiceBoards.difficulty.custom")}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t("choiceBoards.difficulty.customDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Distribution Input */}
      {isCustomDistributionSelected() && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            Özel Zorluk Dağılımı
          </label>
          <textarea
            value={data.difficultyDistribution || ""}
            onChange={(e) => handleCustomDistributionChange(e.target.value)}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            placeholder={t("choiceBoards.difficulty.customPlaceholder")}
            rows={3}
          />
        </div>
      )}
    </div>
  );
};
